import { useEffect, type ReactNode } from 'react';
import { FileCode, Settings } from 'lucide-react';
import { parsePersonalizationConfig } from '../../lib/personalization';
import {
  datetimeLocalToScheduledFor,
  scheduledForToDatetimeLocal,
} from '../../lib/newsletterSchedule';
import { channelLabel, channelsToKeyArray, type ChannelRow } from '../../lib/channels';
import PostmarkSignatureSelect from '../PostmarkSignatures/PostmarkSignatureSelect';
import type { PostmarkTemplate } from '../Automations/types';
import Tooltip from '../Common/Tooltip';
import NewsletterStatusHelpPopover from './NewsletterStatusHelpPopover';
import NewsletterPlannedSendHelpPopover from './NewsletterPlannedSendHelpPopover';
import NewsletterAiPersonalizationHelpPopover from './NewsletterAiPersonalizationHelpPopover';
import NewsletterEmailEnvelope from './NewsletterEmailEnvelope';
import {
  isNewsletterStatusLocked,
  normalizeNewsletterStatus,
  NEWSLETTER_SETTINGS_STATUS_OPTIONS,
  newsletterStatusLabel,
} from '../../lib/newsletterStatus';

type NewsletterDraftRow = Record<string, unknown> & {
  name?: string | null;
  subtitle?: string | null;
  status?: string | null;
  scheduled_for?: string | null;
  channels?: unknown;
  email_subject?: string | null;
  postmark_template_alias?: string | null;
  postmark_signature_id?: number | null;
  personalization_config?: { enabled?: boolean; min_score?: number } | null;
  audience_email_status?: string | null;
};

const AUDIENCE_EMAIL_STATUS_OPTIONS = [
  {
    value: 'confirmed',
    label: 'Confirmed',
    hint: 'Active confirmed subscribers only (default).',
  },
  {
    value: 'unsubscribed',
    label: 'Unsubscribed',
    hint: 'Only people who opted out — e.g. a win-back or required notice.',
  },
  {
    value: 'all',
    label: 'All (except unsubscribed)',
    hint: 'Every subscriber who has not opted out — pending and confirmed included.',
  },
] as const;

type AudienceEmailStatus = (typeof AUDIENCE_EMAIL_STATUS_OPTIONS)[number]['value'];

function parseAudienceEmailStatus(raw: unknown): AudienceEmailStatus {
  const v = typeof raw === 'string' ? raw.trim() : '';
  return AUDIENCE_EMAIL_STATUS_OPTIONS.some((o) => o.value === v)
    ? (v as AudienceEmailStatus)
    : 'confirmed';
}

function SettingsSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700/80">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      </div>
      <div className="px-5 py-4 space-y-4">{children}</div>
    </section>
  );
}

const fieldLabel =
  'block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2';
const fieldInput =
  'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500';

export interface NewsletterSettingsTabProps {
  draft: NewsletterDraftRow;
  setDraft: React.Dispatch<React.SetStateAction<NewsletterDraftRow>>;
  channelCatalog: ChannelRow[];
  availableChannels: string[];
  /** Live recipient total for the current channels + email status selection. */
  audienceCount: number | null;
  /** Per-channel subscriber counts under the current email status. */
  channelCounts: Record<string, number>;
  postmarkTemplates: PostmarkTemplate[];
  postmarkTemplatesLoading: boolean;
  postmarkTemplatesError: string | null;
  onLoadPostmarkTemplates: () => void;
  onOpenTemplatePicker: () => void;
  onRequestRemoveTemplate: () => void;
}

export default function NewsletterSettingsTab({
  draft,
  setDraft,
  channelCatalog,
  availableChannels,
  audienceCount,
  channelCounts,
  postmarkTemplates,
  postmarkTemplatesLoading,
  postmarkTemplatesError,
  onLoadPostmarkTemplates,
  onOpenTemplatePicker,
  onRequestRemoveTemplate,
}: NewsletterSettingsTabProps) {
  useEffect(() => {
    onLoadPostmarkTemplates();
  }, [onLoadPostmarkTemplates]);

  const statusSlug = normalizeNewsletterStatus(draft.status as string | null | undefined);
  const statusLocked = isNewsletterStatusLocked(statusSlug);
  const selectedChannels = channelsToKeyArray(draft.channels);
  const alias = ((draft.postmark_template_alias as string | null) ?? '').trim();
  const usingTemplate = alias.length > 0;
  const sendMode: 'template' | 'raw' = usingTemplate ? 'template' : 'raw';
  const personalization = parsePersonalizationConfig(draft.personalization_config);
  const audienceEmailStatus = parseAudienceEmailStatus(draft.audience_email_status);
  const audienceEmailStatusOption =
    AUDIENCE_EMAIL_STATUS_OPTIONS.find((o) => o.value === audienceEmailStatus) ??
    AUDIENCE_EMAIL_STATUS_OPTIONS[0];

  const selectedTemplate =
    usingTemplate
      ? postmarkTemplates.find((t) => String(t.templateId) === alias || t.alias === alias) ?? null
      : null;

  return (
    <div className="space-y-3 w-full">
      <SettingsSection title="Basics">
        <div>
          <label htmlFor="newsletter-settings-name" className={fieldLabel}>
            Name
          </label>
          <input
            id="newsletter-settings-name"
            type="text"
            value={typeof draft.name === 'string' ? draft.name : ''}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            onBlur={(e) => {
              const trimmed = e.target.value.trim();
              setDraft((d) => ({ ...d, name: trimmed || null }));
            }}
            placeholder="Newsletter name"
            className={fieldInput}
          />
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Internal label shown in lists and the calendar — not visible to subscribers.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className={`${fieldLabel} mb-0`}>Status</label>
              <NewsletterStatusHelpPopover />
            </div>
            {statusLocked ? (
              <div className={`${fieldInput} bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-200`}>
                {newsletterStatusLabel(statusSlug)}
                <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 font-normal normal-case">
                  Use Purge scheduled to return to Draft.
                </span>
              </div>
            ) : (
              <select
                value={statusSlug}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    status: normalizeNewsletterStatus(e.target.value),
                  }))
                }
                className={fieldInput}
              >
                {NEWSLETTER_SETTINGS_STATUS_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className={`${fieldLabel} mb-0`}>Planned send date &amp; time</label>
              <NewsletterPlannedSendHelpPopover />
            </div>
            <input
              type="datetime-local"
              value={scheduledForToDatetimeLocal(draft.scheduled_for as string)}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  scheduled_for: datetimeLocalToScheduledFor(e.target.value),
                }))
              }
              className={fieldInput}
            />
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Browser timezone. See help for how this interacts with Draft vs Scheduled.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className={`${fieldLabel} mb-0`}>AI personalization</label>
              <NewsletterAiPersonalizationHelpPopover />
            </div>
            <div className={`${fieldInput} min-h-[42px] flex flex-col justify-center gap-2 py-2`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={personalization.enabled}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      personalization_config: {
                        ...parsePersonalizationConfig(d.personalization_config),
                        enabled: e.target.checked,
                      },
                    }))
                  }
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm text-gray-800 dark:text-gray-200 font-medium">
                  Per subscriber
                </span>
              </label>
              {personalization.enabled ? (
                <div className="flex items-center gap-2 pl-6">
                  <label
                    htmlFor="newsletter-personalization-min-score"
                    className="text-xs text-gray-600 dark:text-gray-400 shrink-0"
                  >
                    Min score
                  </label>
                  <input
                    id="newsletter-personalization-min-score"
                    type="number"
                    min={0}
                    max={100}
                    value={personalization.min_score}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        personalization_config: {
                          ...parsePersonalizationConfig(d.personalization_config),
                          min_score: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
                        },
                      }))
                    }
                    className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm"
                  />
                </div>
              ) : null}
            </div>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Tunes copy per subscriber before send when profile score meets the minimum.
            </p>
          </div>
        </div>
      </SettingsSection>

      <NewsletterEmailEnvelope
        emailSubject={(draft.email_subject as string) ?? ''}
        preheader={(draft.subtitle as string) ?? ''}
        newsletterName={typeof draft.name === 'string' ? draft.name : ''}
        onEmailSubjectChange={(value) => setDraft((d) => ({ ...d, email_subject: value }))}
        onPreheaderChange={(value) => setDraft((d) => ({ ...d, subtitle: value }))}
      />

      <SettingsSection title="Audience">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_240px] gap-4 md:gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <label className={`${fieldLabel} mb-0`}>Channels</label>
              {selectedChannels.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, channels: [] }))}
                  className="text-[11px] font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Clear (send to all)
                </button>
              ) : null}
            </div>
            {availableChannels.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                No channels found on any subscriber yet. This send will go to every subscriber.
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {availableChannels.map((key) => {
                    const active = selectedChannels.includes(key);
                    const count = channelCounts[key] ?? 0;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() =>
                          setDraft((d) => {
                            const current = channelsToKeyArray(d.channels);
                            const next = active
                              ? current.filter((s) => s !== key)
                              : [...current, key];
                            return { ...d, channels: next };
                          })
                        }
                        aria-pressed={active}
                        title={`${count.toLocaleString()} subscriber${count === 1 ? '' : 's'} match the selected email status`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          active
                            ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {channelLabel(key, channelCatalog)}
                        <span
                          className={`inline-flex items-center justify-center min-w-[1.25rem] px-1 py-px rounded-full text-[10px] font-semibold tabular-nums ${
                            active
                              ? 'bg-emerald-600/70 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {count.toLocaleString()}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                  {selectedChannels.length === 0
                    ? 'No channels selected — sends to all active subscribers.'
                    : `Sends to subscribers in any of: ${selectedChannels
                        .map((s) => channelLabel(s, channelCatalog))
                        .join(', ')}.`}
                </p>
              </>
            )}
          </div>
          <div>
            <label htmlFor="newsletter-audience-email-status" className={fieldLabel}>
              Email status
            </label>
            <select
              id="newsletter-audience-email-status"
              value={audienceEmailStatus}
              onChange={(e) =>
                setDraft((d) => ({ ...d, audience_email_status: e.target.value }))
              }
              className={fieldInput}
            >
              {AUDIENCE_EMAIL_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p
              className={`mt-1 text-[11px] ${
                audienceEmailStatus === 'confirmed'
                  ? 'text-gray-500 dark:text-gray-400'
                  : 'text-amber-600 dark:text-amber-400'
              }`}
            >
              {audienceEmailStatusOption.hint}
            </p>
          </div>
        </div>
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-2 text-xs font-medium text-emerald-800 dark:text-emerald-300">
          {audienceCount != null
            ? `${audienceCount.toLocaleString()} subscriber${audienceCount === 1 ? '' : 's'} will receive this send with the current channels and email status.`
            : 'Calculating audience size…'}
        </div>
      </SettingsSection>

      <SettingsSection title="Send format">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              if (sendMode === 'raw') return;
              setDraft((d) => ({ ...d, postmark_template_alias: null }));
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              sendMode === 'raw'
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Raw HTML
          </button>
          <button
            type="button"
            onClick={() => {
              if (sendMode === 'template' && usingTemplate) return;
              if (!usingTemplate) onOpenTemplatePicker();
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              sendMode === 'template'
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Postmark template
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {sendMode === 'raw'
            ? 'Content blocks render to HTML; optional Postmark wrapper is not used. Set subject and preheader in Inbox above.'
            : 'In Postmark, set the template Subject to {{subject}} (and body to {{{body_html}}}). Use {{subtitle}} for the inbox preview line and {{scheduled_email_id}} in the footer if needed. If a value is empty we omit it from the send model so you can fall back in the template, e.g. {{#subject}}{{subject}}{{/subject}}{{^subject}}Your default subject{{/subject}}.'}
        </p>

        {sendMode === 'template' ? (
          <div>
            <div className="flex items-stretch gap-2">
              <div
                className={`flex-1 px-3 py-2 border rounded-lg text-sm flex items-center gap-2 min-h-[38px] ${
                  usingTemplate
                    ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-900/10'
                    : 'border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10'
                }`}
              >
                {usingTemplate ? (
                  <>
                    <FileCode className="w-4 h-4 text-emerald-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      {selectedTemplate ? (
                        <>
                          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {selectedTemplate.name || '(unnamed template)'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
                            id: {selectedTemplate.templateId}
                            {selectedTemplate.alias ? ` · alias: ${selectedTemplate.alias}` : ''}
                          </div>
                        </>
                      ) : postmarkTemplatesLoading ? (
                        <span className="text-gray-500 dark:text-gray-400">
                          Looking up template <span className="font-mono">{alias}</span>…
                        </span>
                      ) : (
                        <span className="text-gray-700 dark:text-gray-200 font-mono break-all">
                          {alias}{' '}
                          <span className="text-xs text-amber-600 dark:text-amber-400 font-sans">
                            (not found in Postmark)
                          </span>
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <span className="text-amber-700 dark:text-amber-300 text-sm">
                    Pick a template to use this mode.
                  </span>
                )}
              </div>
              {!usingTemplate ? (
                <button
                  type="button"
                  onClick={onOpenTemplatePicker}
                  className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  title="Choose template"
                  aria-label="Choose template"
                >
                  <Settings className="w-4 h-4" />
                </button>
              ) : null}
            </div>
            {usingTemplate ? (
              <div className="mt-1.5 flex items-center justify-end gap-3 text-xs">
                <Tooltip label="Pick a different Postmark template for this newsletter">
                  <button
                    type="button"
                    onClick={onOpenTemplatePicker}
                    className="text-emerald-600 dark:text-emerald-400 hover:underline"
                  >
                    Change
                  </button>
                </Tooltip>
                <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">
                  ·
                </span>
                <Tooltip label="Detach this template — the newsletter will send as raw HTML">
                  <button
                    type="button"
                    onClick={onRequestRemoveTemplate}
                    className="text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:underline"
                  >
                    Remove
                  </button>
                </Tooltip>
              </div>
            ) : null}
            {postmarkTemplatesError ? (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                Could not load templates: {postmarkTemplatesError}
              </p>
            ) : null}
          </div>
        ) : null}
      </SettingsSection>

      <SettingsSection title="Sender">
        <PostmarkSignatureSelect
          value={
            typeof draft.postmark_signature_id === 'number'
              ? draft.postmark_signature_id
              : null
          }
          onChange={(signatureId) =>
            setDraft((prev) => ({ ...prev, postmark_signature_id: signatureId }))
          }
          disabled={statusLocked}
        />
      </SettingsSection>
    </div>
  );
}
